/**
 * The poll message bubble (R6, R7): before voting, plain tappable option
 * rows with no tally visible; after voting, share bars, percentages, and
 * the fan's own choice marked, plus the total vote count and, for a
 * multi-response poll, the "Pick as many as you like." note. A
 * single-response poll's rows go read-only after voting, replaced by a
 * "Change your vote" text action that reopens the choice.
 *
 * `poll` is rendered exactly as `/message/list` returned it -- the bar
 * widths and percentages below are computed from `voteCount`/`totalVotes`
 * fields the server already sends, never a client guess at a tally.
 */
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";
import { useState } from "react";

import {
  POLL_CHANGE_VOTE_LABEL,
  POLL_MULTI_RESPONSE_NOTE,
  POLL_VOTE_FAILED_MESSAGE,
} from "./chatExtrasCopy";
import type { typeMessagePoll } from "./mergeMessagePages";
import { resolvePollOptionTap } from "./pollComposeLogic";

export type typePollBubbleProps = {
  poll: typeMessagePoll;
  /** True while this poll's own message is still sending (an optimistic
   *  pending row): voting against option ids this composer invented locally
   *  makes no sense until the server assigns real ones. */
  disabled: boolean;
  isVoting: boolean;
  voteFailed: boolean;
  onVote: (optionIds: string[]) => void;
};

function votePercent(voteCount: number, totalVotes: number): number {
  if (totalVotes <= 0) return 0;
  return Math.round((voteCount / totalVotes) * 100);
}

export const PollBubble = (props: typePollBubbleProps): ReactNode => {
  const { poll } = props;
  // "Change your vote" reopens a single-response poll's rows without a
  // second vote having happened yet -- purely local UI state, cleared the
  // instant the fan taps a different option.
  const [isChangingVote, setIsChangingVote] = useState(false);

  if (poll.options.length === 0) {
    return (
      <p className="text-body text-ink-secondary" data-testid="poll-empty">
        This poll has no options.
      </p>
    );
  }

  const hasVoted = poll.myVoteOptionIds.length > 0;
  const showTallies = hasVoted && !isChangingVote;
  const isInteractive =
    !props.disabled && (!hasVoted || poll.allowMultiple || isChangingVote);

  const handleTap = (optionId: string): void => {
    if (!isInteractive || props.isVoting) return;
    const nextSelection = resolvePollOptionTap({
      allowMultiple: poll.allowMultiple,
      myVoteOptionIds: poll.myVoteOptionIds,
      tappedOptionId: optionId,
    });
    setIsChangingVote(false);
    props.onVote(nextSelection);
  };

  return (
    <div className="flex flex-col gap-2" data-testid="poll-bubble">
      <p className="text-body-strong text-ink">{poll.question}</p>
      {props.isVoting ? (
        <Skeleton shape="control" label="Recording your vote" lines={1} />
      ) : (
        <div className="flex flex-col gap-1">
          {poll.options.map((option) => {
            const isMine = poll.myVoteOptionIds.includes(option.optionId);
            const percent = votePercent(option.voteCount, poll.totalVotes);
            return (
              <button
                key={option.optionId}
                type="button"
                data-testid="poll-option"
                data-selected={isMine ? "true" : "false"}
                disabled={!isInteractive}
                onClick={() => {
                  handleTap(option.optionId);
                }}
                className="relative flex min-h-touch-target w-full items-center justify-between overflow-hidden rounded-sm border border-hairline px-3 text-left text-body disabled:cursor-default"
              >
                {showTallies ? (
                  <span
                    aria-hidden="true"
                    data-testid="poll-option-bar"
                    className="absolute inset-y-0 left-0 bg-primary-tint"
                    style={{ width: `${percent}%` }}
                  />
                ) : null}
                <span className="relative flex items-center gap-2">
                  {isMine ? (
                    <span aria-hidden="true" className="text-primary">
                      ✓
                    </span>
                  ) : null}
                  {option.label}
                </span>
                {showTallies ? (
                  <span
                    className="relative text-caption text-ink-secondary"
                    data-testid="poll-option-percent"
                  >
                    {percent}%
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
      {props.voteFailed ? (
        <p className="text-caption text-error" data-testid="poll-vote-failed">
          {POLL_VOTE_FAILED_MESSAGE}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <span className="text-caption text-ink-tertiary">
          {poll.totalVotes} {poll.totalVotes === 1 ? "vote" : "votes"}
        </span>
        {poll.allowMultiple && hasVoted ? (
          <span className="text-caption text-ink-tertiary">
            {POLL_MULTI_RESPONSE_NOTE}
          </span>
        ) : null}
        {!poll.allowMultiple && hasVoted && !isChangingVote ? (
          <button
            type="button"
            data-testid="poll-change-vote"
            onClick={() => {
              setIsChangingVote(true);
            }}
            className="text-caption text-primary"
          >
            {POLL_CHANGE_VOTE_LABEL}
          </button>
        ) : null}
      </div>
    </div>
  );
};
